import { FormEvent, useRef, useState } from 'react'
import { Page, PageHeader } from '@/components/page'
import { useForm } from '@inertiajs/react'
import { WizardGuide } from '@/components/layout/WizardGuide'
import { WizardLayout } from '../WizardLayout'
import { WizardActions } from '../WizardActions'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { CountryCombobox } from '@/components/ui/country-combobox'
import { InternationalPhoneInput } from '@/components/ui/international-phone-input'
import { firstError } from '@/lib/utils'
import { useBasePath } from '@/lib/paths'
import { ArrowRight, Building2, Trash2, UserRound } from 'lucide-react'
import { Tip } from '@/components/sketch/Tip'
import type { WizardFormationRef } from '@/types'

type CountryOption = { code: string; name: string }

type IndividualFields = {
  id: string | null
  full_name: string
  email: string
  phone: string
  date_of_birth: string
  country_code: string
  state_province: string
  city: string
  postal_code: string
  street_address_1: string
  street_address_2: string
  is_applicant: boolean
}

type PersonRow = IndividualFields & {
  key: string
  principal_kind: 'individual' | 'entity'
  legal_name: string
  registration_number: string
  controlling_person: IndividualFields | null
}

type PersonProp = {
  id: string
  principal_kind: 'individual' | 'entity'
  full_name: string | null
  legal_name: string | null
  email: string | null
  phone: string | null
  date_of_birth: string | null
  country_code: string
  state_province: string | null
  city: string | null
  postal_code: string | null
  street_address_1: string | null
  street_address_2: string | null
  registration_number: string | null
  is_applicant: boolean
  controlling_person: IndividualFields | null
}

type Props = {
  formation: WizardFormationRef
  applicant_email: string
  countries: CountryOption[]
  people: PersonProp[]
}

const EMPTY_INDIVIDUAL: IndividualFields = {
  id: null,
  full_name: '',
  email: '',
  phone: '',
  date_of_birth: '',
  country_code: 'US',
  state_province: '',
  city: '',
  postal_code: '',
  street_address_1: '',
  street_address_2: '',
  is_applicant: false,
}

function emptyIndividual(): PersonRow {
  return {
    ...EMPTY_INDIVIDUAL,
    key: '',
    principal_kind: 'individual',
    legal_name: '',
    registration_number: '',
    controlling_person: null,
  }
}

function emptyEntity(): PersonRow {
  return { ...emptyIndividual(), principal_kind: 'entity', controlling_person: { ...EMPTY_INDIVIDUAL } }
}

function fromController(controller: IndividualFields): IndividualFields {
  return {
    id: controller.id,
    full_name: controller.full_name ?? '',
    email: controller.email ?? '',
    phone: controller.phone ?? '',
    date_of_birth: controller.date_of_birth ?? '',
    country_code: controller.country_code ?? 'US',
    state_province: controller.state_province ?? '',
    city: controller.city ?? '',
    postal_code: controller.postal_code ?? '',
    street_address_1: controller.street_address_1 ?? '',
    street_address_2: controller.street_address_2 ?? '',
    is_applicant: controller.is_applicant ?? false,
  }
}

function toRow(person: PersonProp): PersonRow {
  return {
    key: person.id,
    id: person.id,
    principal_kind: person.principal_kind,
    full_name: person.full_name ?? '',
    email: person.email ?? '',
    phone: person.phone ?? '',
    date_of_birth: person.date_of_birth ?? '',
    country_code: person.country_code ?? 'US',
    state_province: person.state_province ?? '',
    city: person.city ?? '',
    postal_code: person.postal_code ?? '',
    street_address_1: person.street_address_1 ?? '',
    street_address_2: person.street_address_2 ?? '',
    legal_name: person.legal_name ?? '',
    registration_number: person.registration_number ?? '',
    is_applicant: person.is_applicant ?? false,
    controlling_person: person.controlling_person ? fromController(person.controlling_person) : null,
  }
}

function withApplicantEmail(person: PersonRow, signedInEmail: string): PersonRow {
  if (!signedInEmail) return person

  if (person.principal_kind === 'individual' && person.is_applicant) {
    return { ...person, email: signedInEmail }
  }

  if (person.principal_kind === 'entity' && person.controlling_person?.is_applicant) {
    return {
      ...person,
      controlling_person: { ...person.controlling_person, email: signedInEmail },
    }
  }

  return person
}

function withId(id: string | null) {
  return id ? { id } : {}
}

function toPayload(person: PersonRow) {
  if (person.principal_kind === 'entity') {
    const controller = person.controlling_person ?? EMPTY_INDIVIDUAL
    return {
      ...withId(person.id),
      principal_kind: 'entity',
      legal_name: person.legal_name,
      registration_number: person.registration_number,
      country_code: person.country_code,
      email: person.email,
      controlling_person: {
        ...withId(controller.id),
        full_name: controller.full_name,
        email: controller.email,
        phone: controller.phone,
        date_of_birth: controller.date_of_birth,
        country_code: controller.country_code,
        state_province: controller.state_province,
        city: controller.city,
        postal_code: controller.postal_code,
        street_address_1: controller.street_address_1,
        street_address_2: controller.street_address_2,
        is_applicant: controller.is_applicant,
      },
    }
  }
  return {
    ...withId(person.id),
    principal_kind: 'individual',
    full_name: person.full_name,
    email: person.email,
    phone: person.phone,
    date_of_birth: person.date_of_birth,
    country_code: person.country_code,
    state_province: person.state_province,
    city: person.city,
    postal_code: person.postal_code,
    street_address_1: person.street_address_1,
    street_address_2: person.street_address_2,
    is_applicant: person.is_applicant,
  }
}

function CountrySelect({
  value,
  onChange,
  countries,
  id,
  ariaInvalid,
}: {
  value: string
  onChange: (value: string) => void
  countries: CountryOption[]
  id: string
  ariaInvalid?: boolean
}) {
  return (
    <CountryCombobox
      id={id}
      value={value}
      onValueChange={onChange}
      countries={countries}
      ariaInvalid={ariaInvalid}
    />
  )
}

function IndividualFieldsEditor({
  fields,
  onChange,
  countries,
  idPrefix,
  error,
}: {
  fields: IndividualFields
  onChange: (patch: Partial<IndividualFields>) => void
  countries: CountryOption[]
  idPrefix: string
  error: (field: keyof IndividualFields) => string | undefined
}) {
  const isUs = fields.country_code === 'US'
  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-full_name`}>Full legal name</Label>
        <Input
          id={`${idPrefix}-full_name`}
          value={fields.full_name}
          onChange={(e) => onChange({ full_name: e.target.value })}
          placeholder="As it appears on a government ID"
          aria-invalid={!!error('full_name')}
        />
        {error('full_name') && <p className="text-sm text-destructive" role="alert">{error('full_name')}</p>}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-email`}>Email</Label>
          <Input
            id={`${idPrefix}-email`}
            type="email"
            value={fields.email}
            onChange={(e) => onChange({ email: e.target.value })}
            readOnly={fields.is_applicant}
            aria-invalid={!!error('email')}
          />
          {error('email') && <p className="text-sm text-destructive" role="alert">{error('email')}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-phone`}>Phone</Label>
          <InternationalPhoneInput
            id={`${idPrefix}-phone`}
            value={fields.phone}
            onValueChange={(phone) => onChange({ phone })}
            defaultCountryCode={fields.country_code}
            ariaInvalid={!!error('phone')}
            ariaDescribedBy={error('phone') ? `${idPrefix}-phone-error` : undefined}
          />
          {error('phone') && <p id={`${idPrefix}-phone-error`} className="text-sm text-destructive" role="alert">{error('phone')}</p>}
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-date_of_birth`}>Date of birth</Label>
          <Input
            id={`${idPrefix}-date_of_birth`}
            type="date"
            value={fields.date_of_birth}
            onChange={(e) => onChange({ date_of_birth: e.target.value })}
            onBlur={(e) => onChange({ date_of_birth: e.currentTarget.value })}
            aria-invalid={!!error('date_of_birth')}
          />
          {error('date_of_birth') && <p className="text-sm text-destructive" role="alert">{error('date_of_birth')}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-country`}>Country</Label>
          <CountrySelect
            id={`${idPrefix}-country`}
            value={fields.country_code}
            onChange={(country_code) => onChange({ country_code })}
            countries={countries}
            ariaInvalid={!!error('country_code')}
          />
          {error('country_code') && <p className="text-sm text-destructive" role="alert">{error('country_code')}</p>}
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-street_address_1`}>Street address</Label>
        <Input
          id={`${idPrefix}-street_address_1`}
          value={fields.street_address_1}
          onChange={(e) => onChange({ street_address_1: e.target.value })}
          aria-invalid={!!error('street_address_1')}
        />
        {error('street_address_1') && <p className="text-sm text-destructive" role="alert">{error('street_address_1')}</p>}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-street_address_2`}>
          Apartment / suite <span className="text-muted-foreground font-normal">(optional)</span>
        </Label>
        <Input
          id={`${idPrefix}-street_address_2`}
          value={fields.street_address_2}
          onChange={(e) => onChange({ street_address_2: e.target.value })}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-city`}>City</Label>
          <Input id={`${idPrefix}-city`} value={fields.city} onChange={(e) => onChange({ city: e.target.value })} aria-invalid={!!error('city')} />
          {error('city') && <p className="text-sm text-destructive" role="alert">{error('city')}</p>}
        </div>
        {isUs ? (
          <div className="space-y-1.5">
            <Label htmlFor={`${idPrefix}-state`}>State</Label>
            <Input
              id={`${idPrefix}-state`}
              value={fields.state_province}
              onChange={(e) => onChange({ state_province: e.target.value })}
              aria-invalid={!!error('state_province')}
            />
            {error('state_province') && <p className="text-sm text-destructive" role="alert">{error('state_province')}</p>}
          </div>
        ) : (
          <div className="space-y-1.5">
            <Label htmlFor={`${idPrefix}-state`}>State / province <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input
              id={`${idPrefix}-state`}
              value={fields.state_province}
              onChange={(e) => onChange({ state_province: e.target.value })}
            />
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-postal_code`}>Postal code</Label>
          <Input
            id={`${idPrefix}-postal_code`}
            value={fields.postal_code}
            onChange={(e) => onChange({ postal_code: e.target.value })}
            aria-invalid={!!error('postal_code')}
          />
          {error('postal_code') && <p className="text-sm text-destructive" role="alert">{error('postal_code')}</p>}
        </div>
      </div>
    </div>
  )
}

export default function PeopleEdit({ formation, applicant_email: applicantEmail, countries, people: peopleProp }: Props) {
  const basePath = useBasePath()
  const keyRef = useRef(0)
  const nextKey = () => `person-${++keyRef.current}`
  const [people, setPeople] = useState<PersonRow[]>(() =>
    peopleProp.map((person) => withApplicantEmail(toRow(person), applicantEmail))
  )
  const form = useForm<Record<string, never>>({})

  const error = (index: number, field: string) => firstError(form.errors[`people[${index}].${field}`])

  const updatePerson = (key: string, patch: Partial<PersonRow>) => {
    setPeople((prev) => prev.map((p) => (p.key === key ? { ...p, ...patch } : p)))
  }

  const updateController = (key: string, patch: Partial<IndividualFields>) => {
    setPeople((prev) =>
      prev.map((p) =>
        p.key === key && p.controlling_person
          ? { ...p, controlling_person: { ...p.controlling_person, ...patch } }
          : p
      )
    )
  }

  const setApplicant = (key: string, isController: boolean) => {
    const selectedIndex = people.findIndex((person) => person.key === key)
    const selectedEmailError = isController
      ? `people[${selectedIndex}].controlling_person.email`
      : `people[${selectedIndex}].email`

    form.clearErrors('applicant', selectedEmailError)
    setPeople((prev) =>
      prev.map((p) => {
        const selectedIndividual = !isController && p.key === key
        const selectedController = isController && p.key === key

        return {
          ...p,
          email: selectedIndividual ? applicantEmail : p.email,
          is_applicant: selectedIndividual,
          controlling_person: p.controlling_person
            ? {
                ...p.controlling_person,
                email: selectedController ? applicantEmail : p.controlling_person.email,
                is_applicant: selectedController,
              }
            : p.controlling_person,
        }
      })
    )
  }

  const focusNewOwner = (key: string, field: 'full_name' | 'legal_name') => {
    requestAnimationFrame(() => {
      const input = document.getElementById(`${key}-${field}`)
      input?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      input?.focus({ preventScroll: true })
    })
  }

  const addIndividual = () => {
    const key = nextKey()
    setPeople((prev) => [...prev, { ...emptyIndividual(), key }])
    focusNewOwner(key, 'full_name')
  }

  const addEntity = () => {
    const key = nextKey()
    setPeople((prev) => [...prev, { ...emptyEntity(), key }])
    focusNewOwner(key, 'legal_name')
  }

  const removePerson = (key: string) => setPeople((prev) => prev.filter((p) => p.key !== key))

  const individualApplicant = people.find((person) => person.is_applicant)
  const controllingApplicant = people.find((person) => person.controlling_person?.is_applicant)
  const applicantSelection = individualApplicant
    ? `${individualApplicant.key}:individual`
    : controllingApplicant
      ? `${controllingApplicant.key}:controller`
      : ''

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    form.transform(() => ({ people: people.map(toPayload) }))
    form.patch(`${basePath}/formations/${formation.id}/wizard/people`)
  }

  const guide = (
    <WizardGuide title="Who owns the company?">
      <p>
        Add every person or company that will own part of the business. A company owner also needs the
        person who controls it. The person running the filing is added here, never assumed.
      </p>
    </WizardGuide>
  )

  return (
    <WizardLayout
      title="Who owns the company?"
      currentStep="people"
      formationId={formation.id}
      resumeStep={formation.current_wizard_step}
      breadcrumbs={[{ label: 'Businesses', href: 'businesses' }, { label: 'Who owns the company?' }]}
      guide={guide}
      mobileGuide={guide}
      tips={
        <Tip seed="people-one-per-owner">
          Add a row for each owner. One person can hold several roles later — they don't need to be added twice.
        </Tip>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        <Page>
          <PageHeader
            title={<>Who owns the company?</>}
            subtitle={<>Add every individual or legal entity that will own part of the business.</>}
          />

          {firstError(form.errors.people) && (
            <p className="text-sm text-destructive" role="alert">{firstError(form.errors.people)}</p>
          )}
          {firstError(form.errors.applicant) && (
            <p className="text-sm text-destructive" role="alert">{firstError(form.errors.applicant)}</p>
          )}

          {people.length > 0 && (
            <RadioGroup
              value={applicantSelection}
              aria-label="Person running the filing"
              onValueChange={(value) => {
                const [key, kind] = value.split(':')
                setApplicant(key, kind === 'controller')
              }}
            >
            <ul className="space-y-4">
              {people.map((person, index) => {
                const isEntity = person.principal_kind === 'entity'
                return (
                  <li key={person.key} className="rounded-lg border p-5">
                    <div className="flex items-center justify-between gap-3 mb-4">
                      <p className="flex items-center gap-2 text-sm font-medium text-foreground">
                        {isEntity ? (
                          <Building2 className="size-4 text-muted-foreground" aria-hidden="true" />
                        ) : (
                          <UserRound className="size-4 text-muted-foreground" aria-hidden="true" />
                        )}
                        {isEntity ? 'Company owner' : 'Individual owner'} {index + 1}
                      </p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removePerson(person.key)}
                        aria-label={`Remove ${isEntity ? 'company owner' : 'individual owner'} ${index + 1}`}
                      >
                        <Trash2 className="size-4" />
                        Remove
                      </Button>
                    </div>

                    {isEntity ? (
                      <div className="space-y-5">
                        <div className="space-y-1.5">
                          <Label htmlFor={`${person.key}-legal_name`}>Company legal name</Label>
                          <Input
                            id={`${person.key}-legal_name`}
                            value={person.legal_name}
                            onChange={(e) => updatePerson(person.key, { legal_name: e.target.value })}
                            aria-invalid={!!error(index, 'legal_name')}
                          />
                          {error(index, 'legal_name') && <p className="text-sm text-destructive" role="alert">{error(index, 'legal_name')}</p>}
                        </div>
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div className="space-y-1.5">
                            <Label htmlFor={`${person.key}-registration_number`}>Registration number</Label>
                            <Input
                              id={`${person.key}-registration_number`}
                              value={person.registration_number}
                              onChange={(e) => updatePerson(person.key, { registration_number: e.target.value })}
                              aria-invalid={!!error(index, 'registration_number')}
                            />
                            {error(index, 'registration_number') && (
                              <p className="text-sm text-destructive" role="alert">{error(index, 'registration_number')}</p>
                            )}
                          </div>
                          <div className="space-y-1.5">
                            <Label htmlFor={`${person.key}-country`}>Country</Label>
                            <CountrySelect
                              id={`${person.key}-country`}
                              value={person.country_code}
                              onChange={(country_code) => updatePerson(person.key, { country_code })}
                              countries={countries}
                              ariaInvalid={!!error(index, 'country_code')}
                            />
                            {error(index, 'country_code') && (
                              <p className="text-sm text-destructive" role="alert">{error(index, 'country_code')}</p>
                            )}
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor={`${person.key}-email`}>Contact email</Label>
                          <Input
                            id={`${person.key}-email`}
                            type="email"
                            value={person.email}
                            onChange={(e) => updatePerson(person.key, { email: e.target.value })}
                            aria-invalid={!!error(index, 'email')}
                          />
                          {error(index, 'email') && <p className="text-sm text-destructive" role="alert">{error(index, 'email')}</p>}
                        </div>

                        <div className="rounded-lg border bg-muted/40 p-4">
                          <p className="text-sm font-medium text-foreground mb-3">Controlling individual</p>
                          <IndividualFieldsEditor
                            fields={person.controlling_person ?? EMPTY_INDIVIDUAL}
                            onChange={(patch) => updateController(person.key, patch)}
                            countries={countries}
                            idPrefix={`${person.key}-controller`}
                            error={(field) => error(index, `controlling_person.${field}`)}
                          />
                          {error(index, 'controlling_principal') && (
                            <p className="text-sm text-destructive mt-3" role="alert">{error(index, 'controlling_principal')}</p>
                          )}
                          <div className="flex items-center gap-2 rounded-md border bg-background p-3 mt-3">
                            <RadioGroupItem
                              id={`${person.key}-controller-applicant`}
                              value={`${person.key}:controller`}
                            />
                            <label htmlFor={`${person.key}-controller-applicant`} className="text-sm cursor-pointer">
                              This person is running the filing
                            </label>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <IndividualFieldsEditor
                          fields={person}
                          onChange={(patch) => updatePerson(person.key, patch)}
                          countries={countries}
                          idPrefix={person.key}
                          error={(field) => error(index, field)}
                        />
                        <div className="flex items-center gap-2 rounded-md border bg-muted/40 p-3 mt-4">
                          <RadioGroupItem
                            id={`${person.key}-applicant`}
                            value={`${person.key}:individual`}
                          />
                          <label htmlFor={`${person.key}-applicant`} className="text-sm cursor-pointer">
                            This person is running the filing
                          </label>
                        </div>
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
            </RadioGroup>
          )}

          <div className="space-y-3">
            <p className="text-sm font-medium text-foreground">
              {people.length > 0 ? 'Add another owner' : 'Choose an owner type'}
            </p>
            <div className="grid gap-3">
              <Button
                type="button"
                variant="outline"
                className="h-auto items-start justify-start gap-3 p-4 text-left whitespace-normal"
                onClick={addIndividual}
              >
                <UserRound className="mt-0.5 size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span className="space-y-1">
                  <span className="block font-medium text-foreground">Add an individual owner</span>
                  <span className="block text-sm font-normal text-muted-foreground">
                    A person who will directly own part of this new company.
                  </span>
                </span>
              </Button>
              <Button
                type="button"
                variant="outline"
                className="h-auto items-start justify-start gap-3 p-4 text-left whitespace-normal"
                onClick={addEntity}
              >
                <Building2 className="mt-0.5 size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span className="space-y-1">
                  <span className="block font-medium text-foreground">Add a company owner</span>
                  <span className="block text-sm font-normal text-muted-foreground">
                    An existing legal entity that will own part of this company. You’ll also add the person who controls it.
                  </span>
                </span>
              </Button>
            </div>
          </div>

          <WizardActions currentStep="people" formationId={formation.id} disabled={form.processing}>
            <Button type="submit" disabled={form.processing || people.length === 0}>
              {form.processing ? 'Saving…' : 'Continue'}
              <ArrowRight className="size-4" />
            </Button>
          </WizardActions>
        </Page>
      </form>
    </WizardLayout>
  )
}
